//#region src/fixture-resources.ts
/**
* The ONLY resource keys the platform admits, and the refusal is a correctness fence (F-2).
*
* ⛔ EXTENDED RESOURCES (`nvidia.com/gpu`, `amd.com/gpu`, any vendor-domain key) ARE REFUSED.
* A GPU comes from the machine type: `FixtureService.machineType` resolves to a catalog entry
* whose `gpu.count` the operator stamps as the accelerator limit. Admitting `limits['nvidia.com/gpu']`
* would make a THIRD GPU request path (after the deprecated `gpu` block and the machine type) and
* would let a fixture request a GPU limit on a machine type that has none: scheduled never, explained
* nowhere. DRA `resources.claims` is likewise out of scope — these clusters do not run it, so it would
* be a provably-dead branch on day one.
*
* Hugepages are pinned to the two page sizes GKE's COS node image exposes. Widening is a one-line
* change to this pattern + its schema twin; guessing wide is not.
*/
const K8S_RESOURCE_KEY_PATTERN = /^(cpu|memory|ephemeral-storage|hugepages-2Mi|hugepages-1Gi)$/;
/**
* A Kubernetes resource quantity: a non-negative decimal with an optional SI-decimal, binary, or
* sub-unit suffix. Anchored and linear (no nesting, no backtracking) — safe under Ajv's JS regex
* engine and under RE2 should it ever move.
*
* Deliberately NARROWER than K8s: no leading `+`/`-`, no `e`/`E` exponent form. A manifest is
* hand-authored, and `1e3` in a memory field is a typo far more often than an intent.
*/
const K8S_QUANTITY_PATTERN = /^(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:n|u|m|k|M|G|T|P|E|Ki|Mi|Gi|Ti|Pi|Ei)?$/;
/** Longest quantity string {@link parseK8sQuantity} will look at. Mirrors provisioning-svc's
*  `MAX_QUANTITY_LEN` (pod-resources.ts:35) so the two bounds cannot drift apart silently. */
const MAX_K8S_QUANTITY_LEN = 32;
/**
* ⛔ THE KEY SET HERE AND THE SUFFIX ALTERNATION IN {@link K8S_QUANTITY_PATTERN} (and in the
* capture regex inside {@link parseK8sQuantity}) ARE ONE LIST WRITTEN TWICE. They must stay equal:
* `parseK8sQuantity` resolves a matched suffix through this map with a non-null assertion, which is
* sound ONLY while every alternative the regex admits is a key here. `fixture-resources.test.ts`
* exercises one case per key, so a key added here without the matching regex alternative fails
* loudly (the parse returns `null`) rather than silently.
*
* A `Map` (not an object literal) so a suffix that happens to name an `Object.prototype` member
* cannot resolve to anything — same reasoning as `MEMORY_MULTIPLIERS` in pod-resources.ts.
*/
const K8S_QUANTITY_MULTIPLIERS = /* @__PURE__ */ new Map([
	["n", 1e-9],
	["u", 1e-6],
	["m", .001],
	["k", 1e3],
	["M", 1e6],
	["G", 1e9],
	["T", 0xe8d4a51000],
	["P", 0x38d7ea4c68000],
	["E", 0xde0b6b3a7640000],
	["Ki", 1024],
	["Mi", 2 ** 20],
	["Gi", 2 ** 30],
	["Ti", 2 ** 40],
	["Pi", 2 ** 50],
	["Ei", 2 ** 60]
]);
/** Capture form of {@link K8S_QUANTITY_PATTERN} — mantissa in group 1, suffix in group 2. */
const K8S_QUANTITY_CAPTURE = /^((?:0|[1-9][0-9]*)(?:\.[0-9]+)?)(n|u|m|k|M|G|T|P|E|Ki|Mi|Gi|Ti|Pi|Ei)?$/;
/**
* Parse a K8s quantity to a UNIT-AGNOSTIC number. The suffix is a pure multiplier, so `'250m'` → 0.25
* and `'256Mi'` → 268435456; comparing two values of the SAME resource key is therefore correct
* without the parser knowing whether it is looking at CPU or memory.
*
* ⛔ RETURNS `null`, NOT `0`, for anything it cannot parse — the deliberate divergence from
* provisioning-svc's `parseCpuMillicores`/`parseMemoryBytes`, which return `0` because they AGGREGATE
* (a `0` there is a correct contribution to a sum). Here the value feeds a `requests > limits`
* comparison, and the two `0` failure modes are NOT symmetric:
*   - a `0` for an unparseable LIMIT makes every request look oversized ⇒ a spurious error. LOUD, and
*     survivable: someone sees a wrong message and investigates.
*   - a `0` for an unparseable REQUEST makes EVERY comparison pass ⇒ a silent miss. Nothing goes red,
*     the rule reports clean, and the bad manifest ships.
* ⭐ The second is the one that reaches production, which is why the return type is `number | null` and
* not a number with a sentinel. `null` makes "unparseable" its own outcome the caller must handle; the
* caller's contract (`checkResourceBounds`) is to emit NOTHING and leave the diagnosis to the schema's
* own error.
*
* The suffix lookup is a non-null assertion rather than a guarded branch: the regex alternation and
* {@link K8S_QUANTITY_MULTIPLIERS}'s keys are the same list, so a guard would be unreachable by
* construction — an untestable branch, not a safety net. The equality of the two lists is pinned by
* the per-suffix cases in `fixture-resources.test.ts` instead, where a divergence is observable.
*/
function parseK8sQuantity(q) {
	if (typeof q !== "string" || q.length > 32) return null;
	const match = K8S_QUANTITY_CAPTURE.exec(q);
	if (match === null) return null;
	const value = Number.parseFloat(match[1]);
	const suffix = match[2];
	if (suffix === void 0) return value;
	return value * K8S_QUANTITY_MULTIPLIERS.get(suffix);
}
/**
* The exact value of each suffix as a fraction `num / den`, for {@link compareScaledK8sQuantities}.
* The key set is the key set of {@link K8S_QUANTITY_MULTIPLIERS}, written a third time. The lookup
* is a non-null assertion for the reason {@link parseK8sQuantity} gives, and the per-suffix cases
* of `fixture-resources.test.ts` hold the lists equal: a key that this map lacks throws a
* `TypeError` in the case of that suffix.
*/
const K8S_QUANTITY_EXACT_FACTORS = /* @__PURE__ */ new Map([
	["n", {
		num: 1n,
		den: 10n ** 9n
	}],
	["u", {
		num: 1n,
		den: 10n ** 6n
	}],
	["m", {
		num: 1n,
		den: 10n ** 3n
	}],
	["k", {
		num: 10n ** 3n,
		den: 1n
	}],
	["M", {
		num: 10n ** 6n,
		den: 1n
	}],
	["G", {
		num: 10n ** 9n,
		den: 1n
	}],
	["T", {
		num: 10n ** 12n,
		den: 1n
	}],
	["P", {
		num: 10n ** 15n,
		den: 1n
	}],
	["E", {
		num: 10n ** 18n,
		den: 1n
	}],
	["Ki", {
		num: 1n << 10n,
		den: 1n
	}],
	["Mi", {
		num: 1n << 20n,
		den: 1n
	}],
	["Gi", {
		num: 1n << 30n,
		den: 1n
	}],
	["Ti", {
		num: 1n << 40n,
		den: 1n
	}],
	["Pi", {
		num: 1n << 50n,
		den: 1n
	}],
	["Ei", {
		num: 1n << 60n,
		den: 1n
	}]
]);
/** A quantity as the exact fraction `num / den` (`den > 0`), or `null` for the inputs that
*  {@link parseK8sQuantity} refuses. */
function exactK8sQuantity(q) {
	if (typeof q !== "string" || q.length > 32) return null;
	const match = K8S_QUANTITY_CAPTURE.exec(q);
	if (match === null) return null;
	const mantissa = match[1];
	const dot = mantissa.indexOf(".");
	const digits = dot === -1 ? mantissa : `${mantissa.slice(0, dot)}${mantissa.slice(dot + 1)}`;
	const num = BigInt(digits);
	const den = 10n ** BigInt(dot === -1 ? 0 : mantissa.length - dot - 1);
	const suffix = match[2];
	if (suffix === void 0) return {
		num,
		den
	};
	const factor = K8S_QUANTITY_EXACT_FACTORS.get(suffix);
	return {
		num: num * factor.num,
		den: den * factor.den
	};
}
/**
* Compare `leftFactor` times the quantity `left` with `rightFactor` times the quantity `right`,
* exactly.
*
* Who calls it: the publish rules that compare two quantities of one resource key
* (`checkAppPools` in `app-pool-rules.ts`, `checkFixturePlacementAndResources` in
* `fixture-placement-rules.ts`), and `compareQuantity` in `app-pool.ts`. A factor of 1 compares the
* two quantities alone; a node count and a concurrency compare a pool's node total with its ask.
*
* Why exact. {@link parseK8sQuantity} returns a float, and the grammar admits 32 characters. Two
* different quantities can round to one float: `9007199254740992m` and `9007199254740993m` both
* parse to 9007199254740.992. A float compare then reads a larger ask as equal to its ceiling, and
* a rule that refuses the larger ask stays quiet. This function reads the mantissa digits and the
* suffix as one exact fraction, so no two different quantities compare equal.
*
* Inputs: `left` and `right` are any values. Each factor must be a finite number of 0 or more. A
* fractional factor counts at its exact binary value: a publish rule reads a knob that the schema
* refuses as a fraction (`maxNodes: 1.5`), and the rule still reports the shortfall that the
* fraction gives, as the float compare before this function did.
*
* Output: `-1` when the left product is smaller, `0` when the two are equal, `1` when the left
* product is larger. `null` when either quantity is outside the grammar of
* {@link K8S_QUANTITY_PATTERN} or longer than {@link MAX_K8S_QUANTITY_LEN}, which are exactly the
* inputs {@link parseK8sQuantity} answers `null` for, and `null` when either factor is negative,
* `NaN` or infinite. A caller reads `null` as "the schema reports this input" and stays quiet.
*
* Never throws. Pure: no I/O, no state.
*/
function compareScaledK8sQuantities(left, leftFactor, right, rightFactor) {
	const lf = exactFactor(leftFactor);
	const rf = exactFactor(rightFactor);
	if (lf === null || rf === null) return null;
	const l = exactK8sQuantity(left);
	const r = exactK8sQuantity(right);
	if (l === null || r === null) return null;
	const lhs = lf.num * l.num * r.den * rf.den;
	const rhs = rf.num * r.num * l.den * lf.den;
	if (lhs < rhs) return -1;
	return lhs > rhs ? 1 : 0;
}
/**
* A finite number of 0 or more as the exact fraction `num / den`, with `den` a power of 2, or
* `null` for a negative number, `NaN` and an infinity. Every finite double is a binary fraction,
* and a doubling of a double below 2^53 is exact, so the loop ends with an integer `v` and loses
* no bit. Pure.
*/
function exactFactor(x) {
	if (!Number.isFinite(x) || x < 0) return null;
	let v = x;
	let den = 1n;
	while (!Number.isInteger(v)) {
		v *= 2;
		den *= 2n;
	}
	return {
		num: BigInt(v),
		den
	};
}
/**
* ⭐ The platform's default container resources — EXACTLY the literals the operator hardcodes today
* (`zion-operator` `fixture-deployer.ts:3635-3636`), exported so an app author has one discoverable
* source of truth and the operator can READ it instead of inlining it (F-5).
*
* ⛔ THESE NUMBERS ARE NOT A NEW DEFAULT. They are today's, transcribed. Changing the platform default
* for every existing fixture is a separate, deliberate decision and must not ride this change.
*
* ⚠️ Two facts about "today" that this constant does NOT paper over, and that WS-F runtime must fix
* rather than inherit:
*   1. The literal is on the SIMPLE deploy path only. `buildPodSpec` (the advanced path — `containers[]`,
*      `machineType`/`nodeType`, raw tcp/tls, scaleToZero) sets NO resources at all, so those pods run
*      BestEffort. Adopting this constant there is a behaviour CHANGE (BestEffort → Burstable), and a
*      correct one.
*   2. `applyNodeType` (`fixture-pod-template-builder.ts:922`) currently ASSIGNS
*      `target.resources = { limits: { …gpuResourceLimit } }`. Once a manifest can declare resources it
*      MUST merge, or a GPU service's declared requests vanish.
*/
const PLATFORM_DEFAULT_CONTAINER_RESOURCES = Object.freeze({
	requests: Object.freeze({
		cpu: "250m",
		memory: "256Mi"
	}),
	limits: Object.freeze({
		cpu: "500m",
		memory: "512Mi"
	})
});
function readFixtureInfrastructure(manifest) {
	if (manifest === null || manifest === void 0) return void 0;
	return manifest.infrastructure ?? manifest.resources;
}
//#endregion
export { K8S_QUANTITY_PATTERN, K8S_RESOURCE_KEY_PATTERN, MAX_K8S_QUANTITY_LEN, PLATFORM_DEFAULT_CONTAINER_RESOURCES, compareScaledK8sQuantities, parseK8sQuantity, readFixtureInfrastructure };

//# sourceMappingURL=fixture-resources.js.map